import {FC, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {BadgeOutlined, Edit, PhotoCameraOutlined} from '@mui/icons-material';
import {Box, Button, Stack, Typography} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import {NavLink} from 'react-router-dom';
import {usersApi} from 'src/api';
import {useAppDispatch, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import AMPModal from 'src/components/common/AMPModal';
import {ColorField} from 'src/components/common/ColorField';
import {DropZoneModal} from 'src/components/common/DropZoneModal';
import {UserForm} from 'src/components/forms/UserForm';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {EditUserForm} from '../../forms/EditUserForm';
import {CancelButton} from '../../common/CancelButton';
import {SubmitButton} from '../../common/SubmitButton';
import {ErrorIcon} from '../../icons/ErrorIcon';
import {EditUserNameForm} from '../../forms/EditUserNameForm';
import {UserDetailsType} from './UserDetails.types';
import {UserIdentityCard} from '../UserIdentityCard';
import {UserFact} from '../UserIdentityCard/UserIdentityCard.types';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {buildUserFacts} from '../userProfile';
import {deleteModalContentSx} from './UserDetails.sx';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {UserResetWrapper} from '../UserResetWrapper';

type AddUserAvatarMutationTrigger = ReturnType<typeof usersApi.useSetUserAvatarMutation>[0];

const handleTrigger = async (
    file: File[],
    userId: number,
    trigger: AddUserAvatarMutationTrigger,
    dispatch: AppDispatch,
    handleCloseImageModal: () => void,
    handleBlockSubmit: (status: boolean) => void,
    _businessId?: number
) => {
    try {
        handleBlockSubmit(true);
        dispatch(preloaderChangeStatus(true));
        const formData = new FormData();
        formData.append('file', file[0]);
        await trigger({data: formData, userId}).unwrap();
        handleCloseImageModal();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        dispatch(preloaderChangeStatus(false));
        handleBlockSubmit(false);
    }
};

export const UserDetails: FC<UserDetailsType> = ({data}) => {
    const canUpdateAvatar = useCan('profile.updateAvatar');
    const canEditProfile = useCan('profile.update');
    const canEditName = useCan('users.updateName');
    const intl = useIntl();
    const {authInfo} = useAppSelector((state) => state.common);

    const [showEditModal, setShowEditModal] = useState(false);
    const [showEditNameModal, setShowEditNameModal] = useState(false);
    const [open, setOpen] = useState<boolean>(false);
    const [openDropZone, setOpenDropZone] = useState<boolean>(false);
    const [userImage, setUserImage] = useState<File[]>();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const canReadResetPassword = useCan('users.update');

    const dialogTitle = intl.formatMessage({id: 'editUser'});
    const uploadDialogTitle = intl.formatMessage({id: 'addNewImage'});

    const readLocation = useCan('locations.read');

    const dispatch = useAppDispatch();
    const [trigger] = usersApi.useSetUserAvatarMutation();

    const handleShowEditModal = (status: boolean) => () => setShowEditModal(status);
    const handleShowEditNameModal = (status: boolean) => () => setShowEditNameModal(status);
    const [blockSubmit, setBlockSubmit] = useState(false);

    const handleBlockSubmit = (status: boolean) => {
        setBlockSubmit(status);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleCloseImageModal = () => {
        setOpenDropZone(false);
    };

    const handleSetImage = (file: File[] | null) => {
        file && setUserImage(file);
    };

    const handleSubmit = () => {
        userImage &&
        handleTrigger(
            userImage,
            data.id,
            trigger,
            dispatch,
            handleCloseImageModal,
            handleBlockSubmit,
            data.business.id
        );
    };

    const handleShowDeleteModal = (status: boolean) => {
        return () => {
            setShowDeleteModal(status);
        };
    };


    const locationValue = data.location && (readLocation ? (
        <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations, String(data.location.id)])}>
            <Typography
                component="span"
                color="primary.dark"
            >
                {data.location.name}
            </Typography>
        </NavLink>
    ) : data.location.name);

    const facts: UserFact[] = buildUserFacts(data, locationValue);

    return (
        <>
            <UserIdentityCard
                data={data}
                title={`${data.firstName || ''} ${data.lastName || ''}`.trim() || data.emailAddress || ''}
                typeLabel={String(data.userType)}
                facts={facts}
                actions={(
                    <>
                        {/* The avatar endpoint requires profile.updateAvatar, which is not what
                            `userPhoto` publishes - that subject belongs to users.update. The two
                            differ in both directions: a driver holds the first and not the second,
                            a sales manager the second and not the first. */}
                        {canUpdateAvatar && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<PhotoCameraOutlined/>}
                                onClick={() => setOpenDropZone(true)}
                            >
                                <FormattedMessage id="photo"/>
                            </Button>
                        )}
                        {canEditName && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<BadgeOutlined/>}
                                onClick={handleShowEditNameModal(true)}
                            >
                                <FormattedMessage id="edit"/>
                            </Button>
                        )}
                        {canEditProfile && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<Edit/>}
                                onClick={handleShowEditModal(true)}
                            >
                                <FormattedMessage id="edit"/>
                            </Button>
                        )}
                        {(canReadResetPassword || data.id === authInfo.id) && (
                            <UserResetWrapper
                                data={data}
                                toolbar
                            />
                        )}
                    </>
                )}
            />

            <DropZoneModal
                submitIsBlocked={blockSubmit}
                handleClose={handleCloseImageModal}
                open={openDropZone}
                title={uploadDialogTitle}
                handleFile={handleSetImage}
                handleSubmit={handleSubmit}
                isImage
            />
            <AMPModal
                handleClose={handleShowEditModal(false)}
                open={showEditModal}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EditUserForm
                    handleClose={handleShowEditModal(false)}
                    userData={data}
                    handleShowDeleteModal={handleShowDeleteModal}
                />
            </AMPModal>
            <AMPModal
                handleClose={handleShowEditNameModal(false)}
                open={showEditNameModal}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EditUserNameForm
                    handleClose={handleShowEditNameModal(false)}
                    userData={data}
                    handleShowDeleteModal={handleShowDeleteModal}
                />
            </AMPModal>
            <AMPModal
                handleClose={handleShowDeleteModal(false)}
                open={showDeleteModal}
                type="sm"
                fullWidth
                withoutPaddings
                title=""
            >
                <Stack sx={deleteModalContentSx.root}>
                    <ErrorIcon/>
                    <Typography sx={deleteModalContentSx.text}>
                        {intl.formatMessage({id: 'cannotDeleteAccountMsg'})}
                    </Typography>
                </Stack>
                <Box sx={deleteModalContentSx.actions}>
                    <CancelButton
                        text="cancel"
                        handler={handleShowDeleteModal(false)}
                        rounding={false}
                    />
                    <SubmitButton
                        text="ok"
                        handler={handleShowDeleteModal(false)}
                        rounding={false}
                    />
                </Box>
            </AMPModal>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
            >
                <UserForm
                    handleClose={handleCloseModal}
                    businessId={data.business.id}
                />
            </AMPModal>
        </>
    );
};
